/**
 * One question of "Ask the case" (PIVOT02): the question, the answer sentences with provenance chips, what the answer
 * covers, the rows each tool found as a table with a "Promote to graph" action per row, and the tool calls.
 *
 * A pivot is a lead (PIVOT01). Nothing here writes to the graph by itself: the Promote button hands the row up to the panel,
 * which starts the `promote_pivot_rows` operator on the officer's click. Every string the model wrote is rendered as text
 * (React escapes it); the tool-call list reuses the report renderer, which escapes too.
 */
import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { isPromotableRow, type PivotBlock, type PivotRow } from '../../types/pivot';
import { STOP_WORDS, pivotLedgerHtml } from './sections/pivot';
import { resolveCite, shortId } from './pivotCites';

const ROWS_PREVIEW = 25;

export interface PivotAnswerCardProps {
  block: PivotBlock;
  /** Row ids already on the graph (`block.promoted`, plus the ones promoted in this session). */
  promotedIds: ReadonlySet<string>;
  /** The row whose promotion is running now, if any. */
  promotingRowId: string | null;
  /** The officer cannot act on this case (a viewer), or the case is blocked: the button is disabled. */
  canPromote: boolean;
  onPromote: (block: PivotBlock, row: PivotRow) => void;
  defaultOpen?: boolean;
}

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function PivotAnswerCard({ block, promotedIds, promotingRowId, canPromote, onPromote, defaultOpen = false }: PivotAnswerCardProps) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState(false);
  const statements = (block.statements || []).filter((s) => s && Array.isArray(s.cites) && s.cites.length > 0);
  const c = block.counts || {};
  const rows = block.rows || [];
  const shown = showAll ? rows : rows.slice(0, ROWS_PREVIEW);
  const toolbox = promotingRowId !== null;
  const stamp = block.created_at ? block.created_at.replace('T', ' ').replace(/Z$/, ' UTC') : '';

  return (
    <details open={defaultOpen} data-testid="pivot-block" data-pivot-id={block.pivot_id} data-pivot-grade="lead"
      className="mt-2 rounded-md border border-border text-xs">
      <summary className="cursor-pointer px-2 py-1.5 flex flex-wrap items-center gap-2">
        <span data-testid="pivot-question" className="font-medium">{block.question}</span>
        <span className="rounded bg-muted px-1.5 py-0.5" data-testid="pivot-status-chip">{block.status}</span>
        {stamp && <span className="text-muted-foreground">{stamp}</span>}
        <span className="rounded border border-border px-1.5 py-0.5" data-pivot-grade="lead">lead</span>
      </summary>
      <div className="px-2 pb-2">
        {block.status !== 'answered' && (
          <p data-testid="pivot-status" data-pivot-status={block.status} className="mt-1">
            The pivot {block.status === 'stopped' ? 'stopped before it could answer' : block.status === 'off' ? 'did not run' : 'failed'}:{' '}
            {STOP_WORDS[String(block.reason || '')] || block.reason || 'no reason recorded'}. The rows it found are listed below.
          </p>
        )}
        {block.status === 'answered' && (statements.length ? (
          <ol data-testid="pivot-statements" className="mt-1 list-decimal pl-5 space-y-1.5">
            {statements.map((s) => (
              <li key={s.id} data-testid="pivot-statement">
                {s.text}{' '}
                <span className="inline-flex flex-wrap gap-1 align-middle">
                  {s.cites.map((cite) => {
                    const r = resolveCite(block, cite);
                    const key = `${s.id}|${cite}`;
                    return (
                      <button key={cite} type="button" data-testid="pivot-chip" data-cite-kind={r.kind} data-cite={cite}
                        title={`${r.label}: ${r.text}`} aria-expanded={!!open[key]}
                        onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                        className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono hover:bg-accent">
                        {r.kind === 'fact' ? cite : r.kind === 'hash' ? shortId(cite) : r.rowId ? `${r.kind === 'ledger-row' ? 'ledger ' : ''}${r.rowId}` : shortId(cite)}
                      </button>
                    );
                  })}
                </span>
                {s.cites.map((cite) => {
                  const key = `${s.id}|${cite}`;
                  if (!open[key]) return null;
                  const r = resolveCite(block, cite);
                  return (
                    <div key={cite} data-testid="pivot-chip-detail" className="mt-1 rounded bg-muted/60 px-2 py-1 text-muted-foreground">
                      <strong>{r.label}</strong>{r.rowId && r.rowId !== cite ? ` ${r.rowId}` : ''}: {r.text}
                      {r.hashes.length > 0 && <div className="font-mono break-all" data-testid="pivot-chip-hashes">hash {r.hashes.join(', ')}</div>}
                    </div>
                  );
                })}
              </li>
            ))}
          </ol>
        ) : (
          <p data-testid="pivot-empty" className="mt-1">
            The pivot produced no statement that cites the record or its tool reads ({n(c.dropped_uncited)} uncited and{' '}
            {n(c.dropped_unknown_cite)} with a citation outside the record were dropped).
          </p>
        ))}
        {block.coverage_note && (
          <p data-testid="pivot-coverage" className="mt-2"><strong>What this covers:</strong> {block.coverage_note}</p>
        )}
        {statements.length > 0 && (n(c.dropped_uncited) > 0 || n(c.dropped_unknown_cite) > 0 || n(c.dropped_malformed) > 0) && (
          <p data-testid="pivot-dropped" className="mt-1 text-muted-foreground">
            {n(c.dropped_uncited)} statement(s) dropped for citing nothing, {n(c.dropped_unknown_cite)} for a citation outside the record
            {n(c.dropped_malformed) ? `, ${n(c.dropped_malformed)} malformed` : ''}.
          </p>
        )}

        <h4 className="mt-3 font-medium">Rows found ({rows.length})</h4>
        {rows.length === 0 ? <p className="text-muted-foreground">No tool returned a row.</p> : (
          <div className="mt-1 overflow-x-auto">
            <table data-testid="pivot-rows" className="w-full border-collapse text-left">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="pr-2 py-1 font-normal">Row</th><th className="pr-2 font-normal">Tool</th>
                  <th className="pr-2 font-normal">What the tool found</th><th className="pr-2 font-normal">Hash</th>
                  <th className="font-normal">Graph</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const on = promotedIds.has(r.id);
                  const can = isPromotableRow(r);
                  return (
                    <tr key={r.id} data-testid="pivot-row" data-row-id={r.id} className="border-t border-border align-top">
                      <td className="pr-2 py-1 font-mono">{r.id}</td>
                      <td className="pr-2">{r.tool}{r.fields?.source === 'ledger' ? ' (ledger)' : ''}</td>
                      <td className="pr-2 break-words">{r.text}</td>
                      <td className="pr-2 font-mono break-all">
                        {(r.hashes || []).slice(0, 3).map((h) => <div key={h} title={h}>{shortId(h)}</div>)}
                        {(r.hashes || []).length > 3 && <div className="text-muted-foreground">+{r.hashes.length - 3} more</div>}
                      </td>
                      <td className="whitespace-nowrap">
                        {on ? (
                          <span data-testid="pivot-on-graph" className="inline-flex items-center gap-1 text-green-600"><Check className="w-3.5 h-3.5" /> On graph</span>
                        ) : can ? (
                          <button type="button" data-tour="case-pivot-promote" data-testid="pivot-promote" data-row-id={r.id}
                            disabled={!canPromote || toolbox} onClick={() => onPromote(block, r)}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-accent disabled:opacity-50">
                            {promotingRowId === r.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Promote to graph
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > ROWS_PREVIEW && (
              <button type="button" data-testid="pivot-rows-more" className="mt-1 underline" onClick={() => setShowAll((v) => !v)}>
                {showAll ? `Show the first ${ROWS_PREVIEW} rows` : `Show all ${rows.length} rows`}
              </button>
            )}
          </div>
        )}

        {(block.ledger || []).length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer">Tool calls ({(block.ledger || []).length})</summary>
            <div className="mt-1" dangerouslySetInnerHTML={{ __html: pivotLedgerHtml(block) }} />
          </details>
        )}
        <p className="mt-2 text-muted-foreground" data-testid="pivot-budget">
          Used {n(block.used?.pages)} of {n(block.budget?.pages)} page reads, {n(block.used?.seconds).toFixed(0)} of {n(block.budget?.seconds)} s,{' '}
          {n(block.used?.rounds)} of {n(block.budget?.rounds)} model rounds.
        </p>
      </div>
    </details>
  );
}
